import React, { useEffect, useState } from 'react';
import AxiosUtils from '../../utils/axios-utils';
import { API } from '../../service/api-route';
import {
  Alert,
  Badge,
  Card,
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  TimePicker,
  InputNumber,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SaveOutlined } from '@ant-design/icons';
import { REQUIRED } from '../../constants/rule-form';
import CustomButton from '../../components/custom-button';
import PageTitle from '../../components/page-title';
import { useCurrentRoles } from '../../hooks/use-current-roles';
import { ROLE_USER } from '../../constants/common';
import {
  CHAT_POLICY_CODE_LABEL_VI,
  CHAT_POLICY_LEGEND_VI,
  CHAT_POLICY_MODE,
  CHAT_POLICY_MODE_OPTIONS,
  CHAT_POLICY_PRESETS_VI,
  CHAT_POLICY_WARNING_VI,
  ChatPolicyCheckResult,
  ChatPolicyMode,
  ChatPolicyPresetRow,
  chatClassLabel,
  isChatPolicyMode,
} from '../../constants/chat-policy';
import dayjs from 'dayjs';
import styles from './styles.module.scss';

type DataPost = {
  usingVoice: string;
  roleSalary: string;
  timeSendNotificationPk: any;
  timeSendSalary: any;
  roleCreateGroup: string;
  enableCode: boolean;
  chatPolicyMode: string;
  chatDmSameGroupUnlock: boolean;
  chatAllGroupAnnouncementOnly: boolean;
  pkStartNotifyEnabled: boolean;
};

// Cột bảng ma trận "Chính sách nhắn tin"; dữ liệu theo chế độ lấy từ
// CHAT_POLICY_PRESETS_VI (phải khớp chat-policy.constants.ts bên server).
const CHAT_POLICY_MATRIX_COLUMNS: ColumnsType<ChatPolicyPresetRow> = [
  { title: 'Người gửi', dataIndex: 'sender', key: 'sender', width: 130 },
  { title: 'Nhắn riêng cho ai', dataIndex: 'dm', key: 'dm' },
  { title: 'Đăng trong nhóm', dataIndex: 'groupPost', key: 'groupPost' },
  { title: 'Tạo nhóm', dataIndex: 'createGroup', key: 'createGroup' },
];

// Kết quả "Kiểm tra nhanh" kèm đúng tham số đã gửi lên server: câu kết luận
// phải mô tả lần bấm Kiểm tra, không đổi theo ô input người dùng gõ tiếp sau đó.
type QuickCheckResult = ChatPolicyCheckResult & {
  requestedAs: string;
  requestedUsername: string;
};

// Giá trị gửi lên server vẫn là mã vai trò (USER, MOD...); chỉ đổi chữ hiển thị.
const ROLE_OPTIONS = [
  { value: ROLE_USER.USER, label: 'Người dùng (USER)' },
  { value: ROLE_USER.CREATOR, label: 'Creator (CREATOR)' },
  { value: ROLE_USER.MOD, label: 'Quản lý (MOD)' },
  { value: ROLE_USER.AGENCY, label: 'Đối tác (AGENCY)' },
  { value: ROLE_USER.ADMIN, label: 'Quản trị (ADMIN)' },
  { value: ROLE_USER.STUDIO_CREATOR, label: 'Studio Creator (STUDIO_CREATOR)' },
];

/**
 * Các tab chỉ chia GIAO DIỆN: mọi Form.Item vẫn nằm trong cùng một <Form> và
 * mọi tab đều forceRender, nên nút Lưu luôn gửi đủ bộ trường như trước.
 */
type SettingTabKey = 'chat' | 'salary' | 'periodic' | 'features';

// Trường nào nằm ở tab nào — để khi lưu lỗi thì mở đúng tab có ô bị lỗi.
const FIELD_TAB: Record<string, SettingTabKey> = {
  usingVoice: 'chat',
  roleCreateGroup: 'chat',
  chatPolicyMode: 'chat',
  chatDmSameGroupUnlock: 'chat',
  chatAllGroupAnnouncementOnly: 'chat',
  allowanceTotal: 'salary',
  lateGraceMinutes: 'salary',
  latePenaltyAmount: 'salary',
  checkinReminderMinutes: 'salary',
  roleSalary: 'salary',
  timeSendSalary: 'salary',
  titlePayment: 'salary',
  contentPayment: 'salary',
  timeSendPaymentInMonth: 'salary',
  timeSendNotificationPk: 'periodic',
  pkStartNotifyEnabled: 'periodic',
  titleDataLive: 'periodic',
  contentDataLive: 'periodic',
  timeSendDataLiveInMonth: 'periodic',
  titleCheckDataLive: 'periodic',
  contentCheckDataLive: 'periodic',
  timeSendCheckDataLiveInMonth: 'periodic',
  enableCode: 'features',
};

const TEXT_RULES = [REQUIRED, { max: 200, message: 'Không được quá 200 kí tự' }];

// Server lưu ngày gửi dạng chuỗi ("5") và cron đọc bằng Number(); ô vẫn là Input
// (gửi chuỗi như cũ), chỉ kiểm tra thêm là số nguyên 1–31.
const DAY_IN_MONTH_RULES = [
  REQUIRED,
  {
    validator: (_: unknown, value: unknown) => {
      if (value === undefined || value === null || String(value).trim() === '') {
        return Promise.resolve();
      }
      const day = Number(String(value).trim());
      return Number.isInteger(day) && day >= 1 && day <= 31
        ? Promise.resolve()
        : Promise.reject(new Error('Ngày trong tháng phải từ 1 đến 31'));
    },
  },
];

const DAY_IN_MONTH_EXTRA =
  'Nhập 1–28 để tháng nào cũng gửi; chọn 29–31 thì tháng không có ngày đó sẽ bị bỏ qua.';

const cardBody = { body: { padding: 20 } };

// Giờ gửi lưu dạng "HH:mm". DB có thể NULL, server trả '' khi chưa có bản ghi setting:
// dayjs(null | '', 'HH:mm') là đối tượng Invalid → ô hiện "Invalid Date", luật REQUIRED
// vẫn cho qua (giá trị là object) và lần Lưu sau ghi chuỗi "Invalid Date" vào DB.
// Rỗng/sai định dạng → để trống ô: REQUIRED buộc chọn giờ trước khi lưu.
const parseTime = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const parsed = dayjs(value.trim(), 'HH:mm');
  return parsed.isValid() ? parsed : undefined;
};

const formatTime = (value: unknown): string | undefined => {
  if (!value) return undefined;
  const parsed = dayjs(value as dayjs.ConfigType);
  return parsed.isValid() ? parsed.format('HH:mm') : undefined;
};

function SettingManager() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingTabKey>('chat');
  const [errorTabs, setErrorTabs] = useState<SettingTabKey[]>([]);

  // PUT /settings chỉ ADMIN. Chỉ khoá nút khi đã biết chắc là không phải ADMIN
  // (đọc vai trò lỗi → roles rỗng → vẫn cho bấm, server tự chặn như trước).
  const { loaded: rolesLoaded, roles, isAdmin } = useCurrentRoles();
  const readOnly = rolesLoaded && roles.length > 0 && !isAdmin;

  // Chế độ đang chọn trên form (chưa lưu) điều khiển bảng ma trận + 2 công tắc.
  // Chưa tải xong setting (undefined) coi như OPEN — đúng mặc định phía server.
  const watchedChatPolicyMode = Form.useWatch('chatPolicyMode', form);
  const currentChatPolicyMode: ChatPolicyMode = isChatPolicyMode(watchedChatPolicyMode)
    ? watchedChatPolicyMode
    : CHAT_POLICY_MODE.OPEN;
  const isOpenMode = currentChatPolicyMode === CHAT_POLICY_MODE.OPEN;
  const isWorkOnlyMode = currentChatPolicyMode === CHAT_POLICY_MODE.WORK_ONLY;

  // "Kiểm tra nhanh": hỏi server theo cấu hình ĐÃ LƯU (không phải form đang sửa).
  const [checkSender, setCheckSender] = useState('');
  const [checkReceiver, setCheckReceiver] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<QuickCheckResult | null>(null);
  const [checkError, setCheckError] = useState('');

  useEffect(() => {
    const fetchSetting = async () => {
      try {
        const res: any = await AxiosUtils.get(API.settings);
        form.setFieldsValue({
          usingVoice: res.data.usingVoice.split(','),
          roleSalary: res.data.roleSalary.split(','),
          roleCreateGroup: res.data.roleCreateGroup ? res.data.roleCreateGroup.split(',') : [],
          enableCode: res.data.enableCode || false,
          chatPolicyMode: res.data.chatPolicyMode || 'OPEN',
          chatDmSameGroupUnlock: !!res.data.chatDmSameGroupUnlock,
          chatAllGroupAnnouncementOnly: !!res.data.chatAllGroupAnnouncementOnly,
          timeSendNotificationPk: parseTime(res.data.timeSendNotificationPk),
          timeSendSalary: parseTime(res.data.timeSendSalary),
          titleDataLive: res.data.titleDataLive,
          contentDataLive: res.data.contentDataLive,
          timeSendDataLiveInMonth: res.data.timeSendDataLiveInMonth,
          titleCheckDataLive: res.data.titleCheckDataLive,
          contentCheckDataLive: res.data.contentCheckDataLive,
          timeSendCheckDataLiveInMonth: res.data.timeSendCheckDataLiveInMonth,
          titlePayment: res.data.titlePayment,
          contentPayment: res.data.contentPayment,
          timeSendPaymentInMonth: res.data.timeSendPaymentInMonth,
          allowanceTotal: res.data.allowanceTotal,
          // Server cũ chưa có 3 cột này → hiện mặc định của server mới.
          lateGraceMinutes: res.data.lateGraceMinutes ?? 5,
          latePenaltyAmount: res.data.latePenaltyAmount ?? 50000,
          checkinReminderMinutes: res.data.checkinReminderMinutes ?? 10,
          // Server cũ chưa có cột → coi như bật (mặc định của server mới).
          pkStartNotifyEnabled: res.data.pkStartNotifyEnabled ?? true,
        });
      } catch (error) {
        notification.error({
          message: 'Không thành công',
          description: 'Có lỗi xảy ra, vui lòng thử lại.',
          duration: 4,
        });
      }
    };

    fetchSetting();
  }, []);

  const onFinish = async (values: DataPost) => {
    // Chỉ gửi "HH:mm" hợp lệ; giờ không hợp lệ → undefined (JSON bỏ trường, server giữ
    // giá trị cũ) thay vì ghi chuỗi "Invalid Date" vào DB.
    values.timeSendNotificationPk = formatTime(values.timeSendNotificationPk);
    values.timeSendSalary = formatTime(values.timeSendSalary);

    values.usingVoice = values.usingVoice.toString();
    values.roleSalary = values.roleSalary.toString();
    values.roleCreateGroup = values.roleCreateGroup.toString();

    // Chính sách nhắn tin: server @IsIn/@IsBoolean và ValidationPipe KHÔNG có
    // transform, nên phải gửi đúng JSON boolean (không toString) và không để
    // undefined khi fetch lỗi. chatAllGroupAnnouncementOnly gửi giá trị thô;
    // ở WORK_ONLY server tự ép true khi thực thi.
    values.chatPolicyMode = isChatPolicyMode(values.chatPolicyMode)
      ? values.chatPolicyMode
      : CHAT_POLICY_MODE.OPEN;
    values.chatDmSameGroupUnlock = !!values.chatDmSameGroupUnlock;
    values.chatAllGroupAnnouncementOnly = !!values.chatAllGroupAnnouncementOnly;
    values.pkStartNotifyEnabled = !!values.pkStartNotifyEnabled;

    setLoading(true);
    try {
      await AxiosUtils.put(API.settings, values);
      setErrorTabs([]);
      notification.success({
        message: 'Thành công',
        description: 'Đã lưu cài đặt.',
        duration: 4,
      });
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  const tabsWithErrors = (): SettingTabKey[] => {
    const keys = form
      .getFieldsError()
      .filter((field) => field.errors.length > 0)
      .map((field) => FIELD_TAB[String(field.name[0])])
      .filter(Boolean);
    return Array.from(new Set(keys));
  };

  // Lưu không qua được kiểm tra: ô lỗi có thể nằm ở tab đang ẩn → mở tab đó
  // (ưu tiên giữ tab hiện tại nếu tab này cũng có lỗi) rồi cuộn tới ô lỗi.
  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
    if (!errorFields.length) return;
    setErrorTabs(tabsWithErrors());
    const target =
      errorFields.find((field) => FIELD_TAB[String(field.name[0])] === activeTab) ?? errorFields[0];
    const tab = FIELD_TAB[String(target.name[0])];
    if (tab) setActiveTab(tab);
    setTimeout(() => form.scrollToField(target.name, { block: 'center' }), 50);
  };

  const onFieldsChange = () => {
    if (errorTabs.length) setErrorTabs(tabsWithErrors());
  };

  const onQuickCheck = async () => {
    const username = checkReceiver.trim();
    const as = checkSender.trim();
    setCheckResult(null);
    if (!username) {
      setCheckError('Nhập username người nhận để kiểm tra.');
      return;
    }
    setChecking(true);
    setCheckError('');
    try {
      // ADMIN mới được dùng `as`; để trống = đánh giá cho chính tài khoản admin.
      const params: Record<string, string> = as ? { username, as } : { username };
      const res: any = await AxiosUtils.get(`${API.settings}/check/chat-policy`, params);
      setCheckResult({ ...res.data, requestedAs: as, requestedUsername: username });
    } catch (error: any) {
      // Interceptor ném response.data của server (vd. 404 User not found) — có
      // statusCode. Mất mạng thì interceptor đọc error.response.data trên
      // undefined → TypeError "Cannot read properties…" (hoặc "Network Error"
      // của axios); không đưa chữ kỹ thuật đó lên màn hình.
      const message = error?.message;
      const looksLikeServerBody =
        typeof error?.statusCode === 'number' ||
        (typeof message === 'string' && !/Cannot read properties|Network Error/.test(message));
      setCheckError(
        looksLikeServerBody && message
          ? String(message)
          : 'Không kiểm tra được (username không tồn tại hoặc mất kết nối).'
      );
    } finally {
      setChecking(false);
    }
  };

  const onQuickCheckEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter trong ô kiểm tra không được submit cả form setting.
    e.preventDefault();
    onQuickCheck();
  };

  const tabLabel = (key: SettingTabKey, text: string) => (
    <Badge dot={errorTabs.includes(key)} offset={[6, 0]}>
      {text}
    </Badge>
  );

  const chatTab = (
    <>
      <Card title="Vai trò" className={styles.section} styles={cardBody}>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Vai trò được dùng phòng thoại (voice)"
              tooltip="Chỉ các vai trò này dùng được phòng thoại (voice) trên app."
              rules={[REQUIRED]}
              name="usingVoice"
            >
              <Select
                size="large"
                mode="multiple"
                placeholder="Chọn vai trò được dùng phòng thoại"
                options={ROLE_OPTIONS}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Vai trò được tạo nhóm chat"
              tooltip="Vai trò được tự tạo nhóm chat trên app. Chế độ nhắn tin bên dưới có thể chặn thêm."
              rules={[REQUIRED]}
              name="roleCreateGroup"
            >
              <Select
                size="large"
                mode="multiple"
                placeholder="Chọn vai trò được tạo nhóm chat"
                options={ROLE_OPTIONS}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Chính sách nhắn tin" className={styles.section} styles={cardBody}>
        <Form.Item
          label="Chế độ nhắn tin"
          tooltip="Quy định ai được nhắn riêng, đăng trong nhóm và tạo nhóm. Bảng bên dưới đổi theo chế độ đang chọn."
          rules={[REQUIRED]}
          name="chatPolicyMode"
          className={styles.narrowField}
        >
          <Select
            size="large"
            placeholder="Chọn chế độ nhắn tin"
            options={CHAT_POLICY_MODE_OPTIONS}
          />
        </Form.Item>
        <Alert type="info" showIcon message={CHAT_POLICY_LEGEND_VI} style={{ marginBottom: 16 }} />
        <Table
          size="small"
          pagination={false}
          rowKey="key"
          columns={CHAT_POLICY_MATRIX_COLUMNS}
          dataSource={CHAT_POLICY_PRESETS_VI[currentChatPolicyMode]}
          scroll={{ x: 640 }}
          style={{ marginBottom: 16 }}
        />
        <div style={{ marginBottom: 12 }}>
          <Space>
            <Form.Item name="chatDmSameGroupUnlock" valuePropName="checked" noStyle>
              <Switch disabled={isOpenMode} />
            </Form.Item>
            <span>Cùng nhóm thì được nhắn riêng (chỉ áp dụng chế độ Chỉ trao đổi công việc)</span>
          </Space>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Space>
            {/* WORK_ONLY: server ép bật nên chỉ HIỂN THỊ công tắc bật+khoá,
                giá trị thật vẫn nằm trong Form.Item ẩn để không tự đổi cột.
                Gói Form.Item ẩn + công tắc hiển thị vào MỘT phần tử: antd
                render Form.Item hidden thành div display:none, Space vẫn
                chừa một ô (kèm gap) cho nó nên công tắc bị đẩy lệch. */}
            <span>
              <Form.Item
                name="chatAllGroupAnnouncementOnly"
                valuePropName="checked"
                noStyle
                hidden={isWorkOnlyMode}
              >
                <Switch disabled={isOpenMode} />
              </Form.Item>
              {isWorkOnlyMode && <Switch checked disabled />}
            </span>
            <span>
              Nhóm Thông báo chung (ALL): chỉ quản lý được đăng
              {isWorkOnlyMode ? ' — chế độ Chỉ trao đổi công việc luôn bật' : ''}
            </span>
          </Space>
        </div>
        {isOpenMode ? (
          <div className={styles.hint}>
            Hai công tắc trên chỉ có tác dụng khi chọn chế độ khác “Như hiện nay”.
          </div>
        ) : (
          <Alert type="warning" showIcon message={CHAT_POLICY_WARNING_VI} />
        )}
      </Card>

      <Card
        title="Kiểm tra nhanh chính sách nhắn tin"
        extra={<span className={styles.hint}>Theo cấu hình ĐÃ LƯU</span>}
        className={styles.section}
        styles={cardBody}
      >
        <div className={styles.hint} style={{ marginBottom: 12 }}>
          Xem một người có nhắn riêng được cho người khác không. Để trống người gửi = kiểm tra cho
          chính bạn (chỉ ADMIN chọn được người gửi khác).
        </div>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input
            size="large"
            placeholder="Người gửi (username, trống = bạn)"
            value={checkSender}
            onChange={(e) => setCheckSender(e.target.value)}
            onPressEnter={onQuickCheckEnter}
            style={{ width: 320 }}
          />
          <Input
            size="large"
            placeholder="Người nhận (username)"
            value={checkReceiver}
            onChange={(e) => setCheckReceiver(e.target.value)}
            onPressEnter={onQuickCheckEnter}
            style={{ width: 260 }}
          />
          <CustomButton
            bgColor="third-green"
            text="Kiểm tra"
            loading={checking}
            onClick={onQuickCheck}
          />
        </Space>
        {checkError && <Alert type="error" showIcon message={checkError} />}
        {checkResult && (
          <Alert
            type={checkResult.target?.canDm ? 'success' : 'error'}
            showIcon
            message={
              checkResult.target?.canDm
                ? `OK — ${checkResult.requestedAs || 'bạn'} nhắn riêng được cho ${
                    checkResult.target?.username ?? checkResult.requestedUsername
                  }`
                : `REJECT — ${checkResult.target?.code ?? 'CHAT_POLICY_DM_NOT_ALLOWED'}`
            }
            description={
              <div>
                <div>
                  Chế độ đang lưu: <b>{checkResult.mode}</b> · Người gửi:{' '}
                  <b>{chatClassLabel(checkResult.class)}</b> · Người nhận:{' '}
                  <b>{chatClassLabel(checkResult.target?.class)}</b>
                </div>
                {checkResult.target && !checkResult.target.canDm && checkResult.target.code && (
                  <div>
                    {CHAT_POLICY_CODE_LABEL_VI[checkResult.target.code] ?? checkResult.target.code}
                  </div>
                )}
                <div>
                  Người gửi {checkResult.canCreateGroup ? 'được' : 'không được'} tạo nhóm
                  {(checkResult.dmAllowedClasses ?? []).length > 0
                    ? ` · nhắn không cần kết bạn cho: ${(checkResult.dmAllowedClasses ?? [])
                        .map((cls) => chatClassLabel(cls))
                        .join(', ')}`
                    : ''}
                </div>
              </div>
            }
          />
        )}
      </Card>
    </>
  );

  const salaryTab = (
    <>
      <Card title="Nghỉ phép" className={styles.section} styles={cardBody}>
        <Form.Item
          label="Số ngày phép mỗi tháng"
          extra="Số ngày nghỉ phép mặc định mỗi người được dùng trong một tháng (áp cho tháng mới)."
          rules={[REQUIRED]}
          name="allowanceTotal"
          className={styles.narrowField}
        >
          <InputNumber size="large" style={{ width: '100%' }} min={1} />
        </Form.Item>
      </Card>

      <Card title="Đi trễ & nhắc chấm công" className={styles.section} styles={cardBody}>
        <Row gutter={24}>
          <Col xs={24} lg={8}>
            <Form.Item
              label="Cho phép trễ (phút)"
              extra="Vào ca muộn tối đa bấy nhiêu phút vẫn tính đúng giờ. App đếm lùi đúng khoảng này sau giờ vào ca."
              rules={[REQUIRED]}
              name="lateGraceMinutes"
            >
              <InputNumber size="large" style={{ width: '100%' }} min={0} max={120} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            {/* Cột latePenaltyAmount giữ nguyên trong DB nhưng giờ chỉ là công tắc
                (> 0 = bật): app/push không hiện số tiền nữa và trễ không trừ lương
                (Điều 127 BLLĐ) — trễ không được Quỹ trễ bù thì mất chuyên cần,
                tính trong file Bảng công & lương. */}
            <Form.Item
              label="Nhắc “xác nhận trễ ca” trên app"
              extra="Khi bật: app và thông báo nhắc ghi “trễ ca dùng 1 lượt Quỹ trễ, hết lượt là mất chuyên cần”. Không trừ tiền vào lương."
              name="latePenaltyAmount"
              valuePropName="checked"
              getValueProps={(v) => ({ checked: Number(v) > 0 })}
              normalize={(checked: boolean) => (checked ? 50000 : 0)}
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item
              label="Nhắc trước giờ vào ca (phút)"
              extra="Gửi thông báo đẩy trước giờ vào ca và thêm một lần đúng giờ vào ca cho người chưa chấm công. Đặt 0 để tắt."
              rules={[REQUIRED]}
              name="checkinReminderMinutes"
            >
              <InputNumber size="large" style={{ width: '100%' }} min={0} max={120} precision={0} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card
        title="Thông báo hằng tháng: người nhận & giờ gửi"
        className={styles.section}
        styles={cardBody}
      >
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Nhóm nhận thông báo lương"
              tooltip="Dùng chung cho cả 3 thông báo hằng tháng: Data Live, Check Data Live và Thanh toán lương."
              rules={[REQUIRED]}
              name="roleSalary"
            >
              <Select
                size="large"
                mode="multiple"
                placeholder="Chọn vai trò nhận thông báo"
                options={ROLE_OPTIONS}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Giờ gửi thông báo hằng tháng"
              tooltip="Cả 3 thông báo hằng tháng đều gửi vào giờ này, vào ngày đã chọn cho từng thông báo."
              rules={[REQUIRED]}
              name="timeSendSalary"
            >
              <TimePicker
                size="large"
                format={'HH:mm'}
                showSecond={false}
                placeholder="Chưa đặt — chọn giờ gửi"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Thông báo thanh toán lương" className={styles.section} styles={cardBody}>
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Form.Item label="Tiêu đề" name="titlePayment" rules={TEXT_RULES}>
              <Input placeholder="Nhập tiêu đề" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item
              label="Ngày gửi trong tháng"
              name="timeSendPaymentInMonth"
              rules={DAY_IN_MONTH_RULES}
              extra={DAY_IN_MONTH_EXTRA}
            >
              <Input placeholder="VD: 10" size="large" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Nội dung" name="contentPayment" rules={TEXT_RULES}>
              <Input placeholder="Nhập nội dung" size="large" />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </>
  );

  const periodicTab = (
    <>
      <Alert
        type="info"
        showIcon
        className={styles.section}
        message="Thông báo Data Live và Check Data Live gửi vào giờ và tới nhóm người nhận đã chọn ở tab “Chấm công & lương”."
      />

      <Card title="Thông báo PK hằng ngày" className={styles.section} styles={cardBody}>
        <Form.Item
          label="Giờ gửi"
          extra="Mỗi ngày vào giờ này, nếu hôm đó có trận PK, app gửi thông báo tới mọi người dùng."
          rules={[REQUIRED]}
          name="timeSendNotificationPk"
          className={styles.narrowField}
        >
          <TimePicker
            size="large"
            format={'HH:mm'}
            showSecond={false}
            placeholder="Chưa đặt — chọn giờ gửi"
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Card>

      <Card title="Thông báo khi trận PK bắt đầu" className={styles.section} styles={cardBody}>
        <Form.Item
          label="Cho phép gửi"
          extra="Công tắc tổng. Bật: trận nào được bật “Gửi thông báo khi trận bắt đầu” (trong Quản lý PK) sẽ gửi thông báo thả xuống tới các nhóm đã chọn đúng lúc trận bắt đầu. Tắt: không trận nào gửi."
          name="pkStartNotifyEnabled"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Card>

      <Card title="Thông báo Data Live" className={styles.section} styles={cardBody}>
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Form.Item label="Tiêu đề" name="titleDataLive" rules={TEXT_RULES}>
              <Input placeholder="Nhập tiêu đề" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item
              label="Ngày gửi trong tháng"
              name="timeSendDataLiveInMonth"
              rules={DAY_IN_MONTH_RULES}
              extra={DAY_IN_MONTH_EXTRA}
            >
              <Input placeholder="VD: 1" size="large" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Nội dung" name="contentDataLive" rules={TEXT_RULES}>
              <Input placeholder="Nhập nội dung" size="large" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Thông báo Check Data Live" className={styles.section} styles={cardBody}>
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Form.Item label="Tiêu đề" name="titleCheckDataLive" rules={TEXT_RULES}>
              <Input placeholder="Nhập tiêu đề" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item
              label="Ngày gửi trong tháng"
              name="timeSendCheckDataLiveInMonth"
              rules={DAY_IN_MONTH_RULES}
              extra={DAY_IN_MONTH_EXTRA}
            >
              <Input placeholder="VD: 5" size="large" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Nội dung" name="contentCheckDataLive" rules={TEXT_RULES}>
              <Input placeholder="Nhập nội dung" size="large" />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </>
  );

  const featuresTab = (
    <Card title="Đăng ký tài khoản" className={styles.section} styles={cardBody}>
      <Form.Item
        label="Bắt buộc mã công ty khi nhân viên đăng ký"
        extra="Bật: nhân viên phải nhập đúng mã công ty trên app thì mới chọn được phòng ban. Tắt: đăng ký không cần mã."
        name="enableCode"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
    </Card>
  );

  return (
    <div>
      <PageTitle title="Cài đặt chung" />
      {readOnly && (
        <Alert
          type="info"
          showIcon
          className={styles.section}
          message="Bạn chỉ có quyền xem trang này — chỉ ADMIN lưu được cài đặt."
        />
      )}
      <Form
        name="form"
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onFieldsChange={onFieldsChange}
        autoComplete="off"
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as SettingTabKey)}
          items={[
            {
              key: 'chat',
              label: tabLabel('chat', 'Chat & nhóm'),
              forceRender: true,
              children: chatTab,
            },
            {
              key: 'salary',
              label: tabLabel('salary', 'Chấm công & lương'),
              forceRender: true,
              children: salaryTab,
            },
            {
              key: 'periodic',
              label: tabLabel('periodic', 'Thông báo định kỳ'),
              forceRender: true,
              children: periodicTab,
            },
            {
              key: 'features',
              label: tabLabel('features', 'Tính năng'),
              forceRender: true,
              children: featuresTab,
            },
          ]}
        />

        <div className={styles.footer}>
          <span className={styles.hint}>Một lần lưu áp dụng cho cả 4 tab.</span>
          <CustomButton
            bgColor="secondary-green"
            text="Lưu cài đặt"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={loading}
            disabled={readOnly}
          />
        </div>
      </Form>
    </div>
  );
}

export default SettingManager;
